import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { api, adminApi } from '../../services/api';

const statusOptions = ['Confirmed', 'Packing', 'Packed', 'Dispatched', 'Delivered', 'Returned', 'Refunded', 'Cancelled'];

const OrderDetails = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [deliveryPayment, setDeliveryPayment] = useState(null);
  const [handover, setHandover] = useState({ otp: '', receivedByName: '', receivedByPhone: '', relation: 'self', note: '' });

  useEffect(() => {
    api.get(`/orders/${id}`)
      .then((response) => setOrder(response.data?.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [id]);

  const updateStatus = async (status) => {
    setMessage('');
    if (status === 'Delivered') {
      await initiateDelivery();
      return;
    }
    try {
      const response = await adminApi.updateOrderStatus(id, { status, note: `Admin updated status to ${status}` });
      setOrder(response.data?.data);
      setMessage('Order status updated.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not update order status.');
    }
  };

  const initiateDelivery = async () => {
    setMessage('');
    setDeliveryPayment(null);
    try {
      const response = await adminApi.initiateDeliveryCompletion(id);
      if (response.data?.requiresPayment) {
        setOrder(response.data?.data?.order);
        setDeliveryPayment(response.data?.data?.paymentOrder);
        setMessage('Remaining payment is required before OTP delivery confirmation.');
      } else {
        setOrder(response.data?.data);
        setMessage('Delivery OTP sent to customer email. Verify OTP to mark delivered.');
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not start delivery completion.');
    }
  };

  const updatePayment = async () => {
    setMessage('');
    try {
      const response = await adminApi.updateOrderPayment(id, {
        receivedAmount: Number(paymentAmount || order.paymentSettlement?.balanceDue || 0),
        mode: order.fulfillment?.type === 'pickup' ? 'store' : 'cod'
      });
      setOrder(response.data?.data);
      setPaymentAmount('');
      setMessage('Payment received status updated.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not update payment.');
    }
  };

  const verifyHandover = async () => {
    setMessage('');
    try {
      const response = await adminApi.verifyOrderHandover(id, handover);
      setOrder(response.data?.data);
      setHandover({ otp: '', receivedByName: '', receivedByPhone: '', relation: 'self', note: '' });
      setMessage(order.fulfillment?.type === 'pickup' ? 'Pickup verified and completed.' : 'Delivery verified and completed.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not verify handover.');
    }
  };

  if (loading) return <div className="text-center py-12">Loading order...</div>;
  if (!order) return <div className="text-center py-12">Order not found</div>;

  const customer = order.userId || {};
  const address = order.fulfillment?.type === 'pickup' ? order.fulfillment?.storeSnapshot?.address || {} : order.shippingAddress || {};

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Order ${order._id || order.id}`}
        description="Order details and tracking information"
        breadcrumbs={['Home', 'Orders', order._id || order.id]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Order ID</p>
                <p className="text-gray-900 font-medium">{order._id || order.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Order Date</p>
                <p className="text-gray-900 font-medium">{new Date(order.createdAt || Date.now()).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-gray-900 font-medium">Rs. {Number(order.totalAmount || 0).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Balance Due</p>
                <p className="text-gray-900 font-medium">Rs. {Number(order.paymentSettlement?.balanceDue || 0).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Items</p>
                <p className="text-gray-900 font-medium">{order.products?.length || 0}</p>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="text-gray-900 font-medium">{customer.name || order.customerName || 'Customer'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="text-gray-900 font-medium">{customer.email || order.email || ''}</p>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{order.fulfillment?.type === 'pickup' ? 'Pickup Store' : 'Shipping Address'}</h3>
            {order.fulfillment?.type === 'pickup' && <p className="text-gray-900 font-medium">{order.fulfillment?.storeSnapshot?.name}</p>}
            <p className="text-gray-900">{address.fullAddress}, {address.city}, {address.state} - {address.pincode}</p>
          </div>

          <hr className="border-gray-100" />

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Products & Charges</h3>
            <div className="space-y-3 text-sm">
              {(order.products || []).map((item) => (
                <div key={`${item.productId || item.name}-${item.sku}`} className="flex justify-between border-b border-gray-100 pb-2">
                  <span>{item.name} x {item.quantity}<br /><small className="text-gray-500">Rate Rs. {Number(item.price || 0).toLocaleString('en-IN')} Discount {item.discount || 0}% GST {item.gst || 0}%</small></span>
                  <strong>Rs. {Number(item.lineTotal || item.price * item.quantity || 0).toLocaleString('en-IN')}</strong>
                </div>
              ))}
              <div className="flex justify-between"><span>Subtotal</span><strong>Rs. {Number(order.subtotal || 0).toLocaleString('en-IN')}</strong></div>
              <div className="flex justify-between"><span>GST</span><strong>Rs. {Number(order.taxAmount || 0).toLocaleString('en-IN')}</strong></div>
              <div className="flex justify-between"><span>Delivery/Pickup</span><strong>Rs. {Number(order.deliveryCharge || 0).toLocaleString('en-IN')}</strong></div>
              <div className="flex justify-between"><span>Paid</span><strong>Rs. {Number(order.paymentSettlement?.paidAmount || 0).toLocaleString('en-IN')}</strong></div>
              <div className="flex justify-between"><span>Balance</span><strong>Rs. {Number(order.paymentSettlement?.balanceDue || 0).toLocaleString('en-IN')}</strong></div>
              <div className="flex justify-between text-base"><span>Total</span><strong>Rs. {Number(order.totalAmount || 0).toLocaleString('en-IN')}</strong></div>
            </div>
          </div>

          <hr className="border-gray-100" />

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tracking</h3>
            <div className="space-y-3">
              {(order.trackingHistory || []).map((entry, index) => (
                <div key={`${entry.status}-${index}`} className="border-l-2 border-amber-500 pl-3 text-sm">
                  <p className="font-semibold text-gray-900">{entry.status}</p>
                  <p className="text-gray-500">{entry.date ? new Date(entry.date).toLocaleString() : 'Pending'}</p>
                  <p className="text-gray-700">{entry.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6 h-fit">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h3>
            <span className="inline-block px-4 py-2 rounded-lg text-sm font-semibold bg-yellow-100 text-yellow-700">
              {order.orderStatus}
            </span>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Payment Status</h4>
            <span className={`inline-block px-4 py-2 rounded-lg text-sm font-semibold ${
              order.paymentInfo?.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              {String(order.paymentInfo?.status || 'pending').charAt(0).toUpperCase() + String(order.paymentInfo?.status || 'pending').slice(1)}
            </span>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Receive Remaining Payment</h4>
            <input
              type="number"
              value={paymentAmount}
              onChange={(event) => setPaymentAmount(event.target.value)}
              placeholder={`Balance Rs. ${Number(order.paymentSettlement?.balanceDue || 0).toLocaleString('en-IN')}`}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2"
            />
            <button onClick={updatePayment} disabled={Number(order.paymentSettlement?.balanceDue || 0) <= 0} className="w-full px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition font-medium text-sm disabled:opacity-50">
              Mark Payment Received
            </button>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">{order.fulfillment?.type === 'pickup' ? 'Pickup OTP Verification' : 'Delivery OTP Verification'}</h4>
            <p className="text-xs text-gray-500 mb-2">
              Start delivery completion first. If balance is pending, collect remaining payment through Razorpay before OTP verification.
            </p>
            <button onClick={initiateDelivery} className="mb-3 w-full px-4 py-2 bg-[#1F2A44] text-white rounded-lg hover:bg-[#2F3B58] transition font-medium text-sm">
              Start Delivery Completion
            </button>
            {deliveryPayment && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <p className="font-semibold">Remaining payment order generated</p>
                <p>Amount: Rs. {Number(deliveryPayment.amountDue || 0).toLocaleString('en-IN')}</p>
                <p>Razorpay Order: {deliveryPayment.id}</p>
              </div>
            )}
            <div className="space-y-2">
              <input value={handover.otp} onChange={(event) => setHandover({ ...handover, otp: event.target.value })} placeholder="Enter OTP" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              <input value={handover.receivedByName} onChange={(event) => setHandover({ ...handover, receivedByName: event.target.value })} placeholder="Received by name" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              <input value={handover.receivedByPhone} onChange={(event) => setHandover({ ...handover, receivedByPhone: event.target.value })} placeholder="Received by phone" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              <select value={handover.relation} onChange={(event) => setHandover({ ...handover, relation: event.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <option value="self">Self</option>
                <option value="family">Family</option>
                <option value="staff">Staff</option>
                <option value="other">Other</option>
              </select>
              <button onClick={verifyHandover} className="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition font-medium text-sm">
                Verify OTP & Complete
              </button>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2">Update Status</h4>
            <div className="space-y-2">
              {statusOptions.map((status) => (
                <button key={status} onClick={() => updateStatus(status)} className="w-full px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition font-medium text-sm">
                  Mark {status}
                </button>
              ))}
              {message && <p className="text-sm text-gray-600">{message}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
